import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonDisplayComponent } from '../../shared/design-system/structured-json-display.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import {
  ConvertQuoteToProposalPayload,
  CreateInsuranceQuotePayload,
  InsuranceApiService,
  InsuranceCoverageItem,
  InsuranceProductListItem,
  InsuranceProductVersionListItem,
  InsuranceQuoteDetail,
  InsuranceProductDetail,
  PriceInsuranceQuotePayload
} from './insurance-api.service';
import { InsuranceAiCopilotPanelComponent } from './insurance-ai-copilot-panel.component';
import {
  BENEFICIARY_ITEM_FIELDS,
  INSURED_PARTY_ITEM_FIELDS,
  PAYMENT_PLAN_OPTIONS,
  serializeProposalPartyArray
} from './insurance-proposal-schemas';
import {
  JsonArrayItem,
  QUOTE_PRICING_SNAPSHOT_FIELDS,
  QUOTE_RISK_OBJECT_ITEM_FIELDS,
  QUOTE_SELECTED_COVERAGE_ITEM_FIELDS,
  coverageSummary,
  defaultItemModel,
  formatBooleanDisplay,
  mergeItemModel,
  parseJsonArray,
  parseRiskObjectsJson,
  parseSelectedCoveragesJson,
  serializeJsonArray
} from './insurance-quote-schemas';

type QuoteTab = 'risk' | 'coverages' | 'premium' | 'documents' | 'history';
type ArrayDialogMode = 'create' | 'edit';

@Component({
  standalone: true,
  selector: 'ain-insurance-quote-detail',
  imports: [
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    RouterLink,
    FormsModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TableModule,
    ButtonModule,
    DialogModule,
    CustomerSelectorComponent,
    PartySelectorComponent,
    CurrencySelectorComponent,
    JalaliDateInputComponent,
    InfoHintComponent,
    InsuranceAiCopilotPanelComponent,
    StructuredJsonFormComponent,
    StructuredJsonDisplayComponent
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('insurance.quoteDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.quotes.help.detail.title'"
        [descriptionKey]="'insurance.quotes.help.detail.description'"
        [bulletKeys]="[
          'insurance.quotes.help.detail.bullets.tabs',
          'insurance.quotes.help.detail.bullets.pricing',
          'insurance.quotes.help.detail.bullets.proposal'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (isCreateMode()) {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/quotes']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveCreate()"></button>
        </div>
        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.quotes.sections.info') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.quotes.help.info.title'"
              [descriptionKey]="'insurance.quotes.help.info.description'"
              [bulletKeys]="[
                'insurance.quotes.help.info.bullets.product',
                'insurance.quotes.help.info.bullets.customer',
                'insurance.quotes.help.info.bullets.dates'
              ]" />
          </div>
          <form class="grid" (ngSubmit)="saveCreate()">
            <label>
              <span>{{ t('insurance.products.fields.legalEntityId') }}</span>
              <ain-party-selector [(ngModel)]="createForm.legalEntityId" name="legalEntityId" />
            </label>
            <label>
              <span>{{ t('insurance.quotes.fields.customer') }}</span>
              <ain-customer-selector [(ngModel)]="createForm.customerAccountId" name="customerAccountId" />
            </label>
            <label>
              <span>{{ t('insurance.policies.fields.product') }}</span>
              <p-select
                [options]="productOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="selectedProductId"
                (ngModelChange)="onProductChanged($event)"
                [showClear]="true"
                [filter]="true"
                appendTo="body"
                name="productId" />
            </label>
            <label>
              <span>{{ t('insurance.quotes.fields.productVersion') }}</span>
              <p-select
                [options]="versionOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="createForm.productVersionId"
                (ngModelChange)="onVersionChanged($event)"
                [showClear]="true"
                [filter]="true"
                appendTo="body"
                name="productVersionId" />
            </label>
            <label>
              <span>{{ t('insurance.quotes.fields.validUntil') }}</span>
              <ain-date-input [(ngModel)]="createForm.validUntilUtc" name="validUntilUtc" />
            </label>
          </form>
        </section>

        <section class="card">
          <div class="lines-header">
            <h4>{{ t('insurance.quoteDetail.sections.riskObjects') }}</h4>
            <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="openRiskObjectCreate()"></button>
          </div>
          @if (riskObjectItems().length === 0) {
            <ain-state type="empty" [title]="t('insurance.quoteDetail.states.noRiskObjects')" />
          } @else {
            <p-table [value]="riskObjectItems()" styleClass="lines-table">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('insurance.quoteDetail.schemas.zoneCode') }}</th>
                  <th>{{ t('insurance.quoteDetail.schemas.description') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row let-i="rowIndex">
                <tr>
                  <td>{{ row.zoneCode || '-' }}</td>
                  <td>{{ row.description || '-' }}</td>
                  <td class="actions-cell">
                    <button pButton type="button" text="true" [label]="t('common.edit')" (click)="openRiskObjectEdit(i)"></button>
                    <button pButton type="button" text="true" severity="danger" [label]="t('common.remove')" (click)="removeRiskObject(i)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>

        <section class="card">
          <div class="lines-header">
            <h4>{{ t('insurance.quoteDetail.sections.selectedCoverages') }}</h4>
            <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="openCoverageCreate()"></button>
          </div>
          @if (selectedCoverageItems().length === 0) {
            <ain-state type="empty" [title]="t('insurance.quoteDetail.states.noSelectedCoverages')" />
          } @else {
            <p-table [value]="selectedCoverageItems()" styleClass="lines-table">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('insurance.quoteDetail.schemas.coverageCode') }}</th>
                  <th>{{ t('insurance.quoteDetail.schemas.sumInsured') }}</th>
                  <th>{{ t('insurance.quoteDetail.schemas.selected') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row let-i="rowIndex">
                <tr>
                  <td>{{ row.coverageCode || '-' }}</td>
                  <td>{{ row.sumInsured ?? '-' }}</td>
                  <td>{{ formatBoolean(row.selected) }}</td>
                  <td class="actions-cell">
                    <button pButton type="button" text="true" [label]="t('common.edit')" (click)="openCoverageEdit(i)"></button>
                    <button pButton type="button" text="true" severity="danger" [label]="t('common.remove')" (click)="removeCoverage(i)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
        @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
      } @else if (!quote()) {
        <ain-state type="error" [title]="t('insurance.states.notFound')" [message]="t('insurance.states.notFoundMessage')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/quotes']">{{ t('catalog.action.backToList') }}</a>
        </div>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.quotes.sections.info') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.quotes.help.info.title'"
              [descriptionKey]="'insurance.quotes.help.info.description'"
              [bulletKeys]="[
                'insurance.quotes.help.info.bullets.product',
                'insurance.quotes.help.info.bullets.customer',
                'insurance.quotes.help.info.bullets.dates'
              ]" />
          </div>
          <div class="summary">
            <span>{{ t('insurance.quotes.fields.quoteNumber') }}: <strong>{{ quote()!.quoteNumber }}</strong></span>
            <span>{{ t('columns.status') }}: <strong>{{ t(statusKey(quote()!.status)) }}</strong></span>
            <span>{{ t('insurance.quotes.fields.validUntil') }}: <strong>{{ quote()!.validUntilUtc | ainDate }}</strong></span>
            <span>{{ t('insurance.quotes.fields.pricingHash') }}: <strong>{{ quote()!.pricingSnapshotHash || '-' }}</strong></span>
          </div>
          <div class="grid">
            <label>
              <span>{{ t('insurance.quotes.fields.customer') }}</span>
              <ain-customer-selector [ngModel]="quote()!.customerAccountId" [disabled]="true" name="viewCustomerAccountId" />
            </label>
            <label>
              <span>{{ t('insurance.quotes.fields.productVersion') }}</span>
              <p-select
                [options]="versionOptions()"
                optionLabel="label"
                optionValue="value"
                [ngModel]="quote()!.productVersionId"
                [disabled]="true"
                appendTo="body"
                name="viewProductVersionId" />
            </label>
            <label>
              <span>{{ t('insurance.quotes.fields.status') }}</span>
              <p-select
                [options]="quoteStatusOptions()"
                optionLabel="label"
                optionValue="value"
                [ngModel]="quote()!.status"
                [disabled]="true"
                appendTo="body"
                name="viewStatus" />
            </label>
          </div>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.quotes.sections.lifecycle') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.quotes.help.lifecycle.title'"
              [descriptionKey]="'insurance.quotes.help.lifecycle.description'"
              [bulletKeys]="['insurance.quotes.help.lifecycle.bullets.convert']" />
          </div>
          <div class="lifecycle-actions">
            <button pButton type="button" [label]="t('insurance.quoteDetail.actions.convertToProposal')" [disabled]="!canConvert()" (click)="openConvertDialog()"></button>
          </div>
          @if (successMessage()) { <p class="success">{{ successMessage() }}</p> }
          @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
        </section>

        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'risk'" (click)="activeTab.set('risk')">{{ t('insurance.quoteDetail.tabs.risk') }}</button>
            <button type="button" [class.active]="activeTab() === 'coverages'" (click)="activeTab.set('coverages')">{{ t('insurance.quoteDetail.tabs.coverages') }}</button>
            <button type="button" [class.active]="activeTab() === 'premium'" (click)="activeTab.set('premium')">{{ t('insurance.quoteDetail.tabs.premium') }}</button>
            <button type="button" [class.active]="activeTab() === 'documents'" (click)="activeTab.set('documents')">{{ t('insurance.quoteDetail.tabs.documents') }}</button>
            <button type="button" [class.active]="activeTab() === 'history'" (click)="activeTab.set('history')">{{ t('insurance.quoteDetail.tabs.history') }}</button>
          </div>

          @if (activeTab() === 'premium') {
            <div class="lines-header">
              <h4>{{ t('insurance.quoteDetail.tabs.premium') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.quotes.help.tabs.premium.title'"
                [descriptionKey]="'insurance.quotes.help.tabs.premium.description'"
                [bulletKeys]="[
                  'insurance.quotes.help.tabs.premium.bullets.price',
                  'insurance.quotes.help.tabs.premium.bullets.hash'
                ]" />
            </div>
            <form class="grid section-gap">
              <label>
                <span>{{ t('insurance.rating.fields.currency') }}</span>
                <ain-currency-selector [(ngModel)]="priceCurrencyCode" name="priceCurrencyCode" />
              </label>
              <label>
                <span>{{ t('insurance.quoteDetail.fields.sumInsured') }}</span>
                <p-inputNumber [(ngModel)]="priceSumInsured" name="priceSumInsured" [min]="0" />
              </label>
              <label>
                <span>{{ t('insurance.quoteDetail.fields.factor') }}</span>
                <p-inputNumber [(ngModel)]="priceFactor" name="priceFactor" [min]="0" [maxFractionDigits]="6" />
              </label>
            </form>
            <div class="actions section-gap">
              <button pButton type="button" [label]="t('insurance.quoteDetail.actions.price')" [disabled]="saving()" (click)="priceQuote()"></button>
            </div>
            <p class="field-hint">{{ t('insurance.quotes.fields.pricingHash') }}: {{ quote()!.pricingSnapshotHash || '-' }}</p>

            <div class="lines-header section-gap">
              <h4>{{ t('insurance.quoteDetail.sections.optionsComparison') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.quotes.help.tabs.options.title'"
                  [descriptionKey]="'insurance.quotes.help.tabs.options.description'"
                  [bulletKeys]="[
                    'insurance.quotes.help.tabs.options.bullets.dialog',
                    'insurance.quotes.help.tabs.options.bullets.save'
                  ]" />
                <button pButton type="button" severity="secondary" [label]="t('insurance.quoteDetail.actions.addOption')" (click)="openOptionCreate()"></button>
                <button pButton type="button" [label]="t('insurance.quoteDetail.actions.saveOptions')" [disabled]="optionDrafts().length === 0 || saving()" (click)="saveOptions()"></button>
              </div>
            </div>
            @if (optionDrafts().length > 0) {
              <p-table [value]="optionDrafts()" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.quoteDetail.fields.optionLabel') }}</th>
                    <th>{{ t('insurance.quoteDetail.fields.sumInsured') }}</th>
                    <th>{{ t('insurance.quoteDetail.fields.factor') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                  <tr>
                    <td>{{ row.optionLabel }}</td>
                    <td>{{ row.sumInsured }}</td>
                    <td>{{ row.factor }}</td>
                    <td class="actions-cell">
                      <button pButton type="button" text="true" severity="danger" [label]="t('common.remove')" (click)="removeOptionDraft(rowIndex)"></button>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            }
            @if (quote()!.options.length === 0 && optionDrafts().length === 0) {
              <ain-state type="empty" [title]="t('insurance.quoteDetail.states.noOptions')" />
            } @else if (quote()!.options.length > 0) {
              <p-table [value]="quote()!.options" dataKey="id" [rows]="10" [paginator]="true" styleClass="lines-table section-gap">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.quoteDetail.fields.optionLabel') }}</th>
                    <th>{{ t('insurance.quoteDetail.fields.coverageSelection') }}</th>
                    <th>{{ t('insurance.quotes.fields.pricingHash') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.optionLabel }}</td>
                    <td>{{ optionCoverageSummary(row.coverageSelectionJson) }}</td>
                    <td>{{ row.pricingSnapshotHash }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          } @else if (activeTab() === 'history') {
            <div class="lines-header">
              <h4>{{ t('insurance.quoteDetail.tabs.history') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.quotes.help.tabs.history.title'"
                [descriptionKey]="'insurance.quotes.help.tabs.history.description'"
                [bulletKeys]="['insurance.quotes.help.tabs.history.bullets.readonly']" />
            </div>
            <div class="summary-grid">
              <label>
                <span>{{ t('insurance.quotes.fields.status') }}</span>
                <p-select
                  [options]="quoteStatusOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [ngModel]="quote()!.status"
                  [disabled]="true"
                  appendTo="body"
                  name="historyStatus" />
              </label>
              <p><strong>{{ t('insurance.quotes.fields.validUntil') }}:</strong> {{ quote()!.validUntilUtc | ainDate }}</p>
              <p><strong>{{ t('insurance.quotes.fields.pricingHash') }}:</strong> {{ quote()!.pricingSnapshotHash || '-' }}</p>
              @if (quote()!.pricedAtUtc) {
                <p><strong>{{ t('insurance.quotes.fields.pricedAt') }}:</strong> {{ quote()!.pricedAtUtc! | ainDate }}</p>
              }
            </div>
          } @else if (activeTab() === 'risk') {
            <div class="lines-header">
              <h4>{{ t('insurance.quoteDetail.tabs.risk') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.quotes.help.tabs.risk.title'"
                [descriptionKey]="'insurance.quotes.help.tabs.risk.description'"
                [bulletKeys]="['insurance.quotes.help.tabs.risk.bullets.structured']" />
            </div>
            @if (riskObjectRows().length === 0) {
              <ain-state type="empty" [title]="t('insurance.quoteDetail.states.noRiskObjects')" />
            } @else {
              <div class="list-grid">
                @for (row of riskObjectRows(); track $index) {
                  <article class="row-card">
                    <ain-structured-json-display
                      [fields]="riskObjectFields"
                      [payloadJson]="riskObjectPayloadJson(row)" />
                  </article>
                }
              </div>
            }
          } @else if (activeTab() === 'coverages') {
            <div class="lines-header">
              <h4>{{ t('insurance.quoteDetail.tabs.coverages') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.quotes.help.tabs.coverages.title'"
                [descriptionKey]="'insurance.quotes.help.tabs.coverages.description'"
                [bulletKeys]="['insurance.quotes.help.tabs.coverages.bullets.structured']" />
            </div>
            @if (selectedCoverageRows().length === 0) {
              <ain-state type="empty" [title]="t('insurance.quoteDetail.states.noSelectedCoverages')" />
            } @else {
              <div class="list-grid">
                @for (row of selectedCoverageRows(); track $index) {
                  <article class="row-card">
                    <ain-structured-json-display
                      [fields]="coverageFields"
                      [payloadJson]="coveragePayloadJson(row)" />
                  </article>
                }
              </div>
            }
          } @else if (activeTab() === 'documents') {
            <div class="lines-header">
              <h4>{{ t('insurance.quoteDetail.tabs.documents') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.quotes.help.tabs.documents.title'"
                [descriptionKey]="'insurance.quotes.help.tabs.documents.description'"
                [bulletKeys]="['insurance.quotes.help.tabs.documents.bullets.snapshot']" />
            </div>
            <ain-structured-json-display
              [fields]="pricingSnapshotFields"
              [payloadJson]="quote()!.pricingSnapshotJson || '{}'"
              [emptyMessage]="t('insurance.quoteDetail.states.noPricingSnapshot')" />
          }
        </section>

        <ain-insurance-ai-copilot-panel entityType="InsuranceQuote" [entityId]="quote()!.id" />

        <p-dialog [header]="t('insurance.quoteDetail.actions.addOption')" [modal]="true" [(visible)]="optionDialogVisible" [style]="{ width: '560px' }">
          <div class="dialog-grid">
            <label class="wide">
              <span>{{ t('insurance.quoteDetail.fields.optionLabel') }}</span>
              <input pInputText [(ngModel)]="optionForm.optionLabel" name="dialogOptionLabel" />
            </label>
            <label>
              <span>{{ t('insurance.quoteDetail.fields.sumInsured') }}</span>
              <p-inputNumber [(ngModel)]="optionForm.sumInsured" name="dialogOptionSumInsured" [min]="0" />
            </label>
            <label>
              <span>{{ t('insurance.quoteDetail.fields.factor') }}</span>
              <p-inputNumber [(ngModel)]="optionForm.factor" name="dialogOptionFactor" [min]="0" [maxFractionDigits]="6" />
            </label>
          </div>
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="optionDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.add')" [disabled]="!optionForm.optionLabel.trim()" (click)="saveOptionDialog()"></button>
          </ng-template>
        </p-dialog>

        <p-dialog [header]="t('insurance.quoteDetail.convertDialog.title')" [modal]="true" [(visible)]="convertDialogVisible" [style]="{ width: '720px' }">
          <div class="dialog-grid">
            <label class="wide">
              <span>{{ t('insurance.proposals.fields.policyholderPartyId') }}</span>
              <ain-party-selector [(ngModel)]="convertForm.policyholderPartyId" name="convertPolicyholderPartyId" />
            </label>
            <label class="wide">
              <span>{{ t('insurance.proposalDetail.fields.paymentPlanPreference') }}</span>
              <p-select
                [options]="paymentPlanOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="convertForm.paymentPlanPreference"
                appendTo="body"
                name="convertPaymentPlan" />
            </label>
          </div>
          <div class="section-gap">
            <div class="lines-header">
              <h4>{{ t('insurance.proposalDetail.sections.insuredParties') }}</h4>
              <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="openInsuredPartyCreate()"></button>
            </div>
            @if (convertInsuredParties().length === 0) {
              <p class="field-hint">{{ t('insurance.proposalDetail.states.noInsuredParties') }}</p>
            } @else {
              <p-table [value]="convertInsuredParties()" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.proposalDetail.schemas.party') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row let-i="rowIndex">
                  <tr>
                    <td><ain-party-selector [ngModel]="row.partyId" [disabled]="true" /></td>
                    <td class="actions-cell">
                      <button pButton type="button" text="true" [label]="t('common.edit')" (click)="openInsuredPartyEdit(i)"></button>
                      <button pButton type="button" text="true" severity="danger" [label]="t('common.remove')" (click)="removeInsuredParty(i)"></button>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            }
          </div>
          <div class="section-gap">
            <div class="lines-header">
              <h4>{{ t('insurance.proposalDetail.sections.beneficiaries') }}</h4>
              <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="openBeneficiaryCreate()"></button>
            </div>
            @if (convertBeneficiaries().length === 0) {
              <p class="field-hint">{{ t('insurance.proposalDetail.states.noBeneficiaries') }}</p>
            } @else {
              <p-table [value]="convertBeneficiaries()" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.proposalDetail.schemas.party') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row let-i="rowIndex">
                  <tr>
                    <td><ain-party-selector [ngModel]="row.partyId" [disabled]="true" /></td>
                    <td class="actions-cell">
                      <button pButton type="button" text="true" [label]="t('common.edit')" (click)="openBeneficiaryEdit(i)"></button>
                      <button pButton type="button" text="true" severity="danger" [label]="t('common.remove')" (click)="removeBeneficiary(i)"></button>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            }
          </div>
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="convertDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('insurance.quoteDetail.actions.convertToProposal')" [disabled]="!convertForm.policyholderPartyId.trim()" (click)="convertToProposal()"></button>
          </ng-template>
        </p-dialog>
      }

      <p-dialog [header]="riskObjectDialogTitle()" [modal]="true" [(visible)]="riskObjectDialogVisible" [style]="{ width: '640px' }">
        <ain-structured-json-form
          [fields]="riskObjectFields"
          [(payloadJson)]="riskObjectDraftJson" />
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="riskObjectDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="saveRiskObjectDialog()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="coverageDialogTitle()" [modal]="true" [(visible)]="coverageDialogVisible" [style]="{ width: '640px' }">
        <ain-structured-json-form
          [fields]="coverageFields"
          [selectOptionOverrides]="coverageCodeSelectOptions()"
          [(payloadJson)]="coverageDraftJson" />
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="coverageDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="saveCoverageDialog()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="partyDialogTitle()" [modal]="true" [(visible)]="partyDialogVisible" [style]="{ width: '560px' }">
        <ain-structured-json-form
          [fields]="partyDialogFields()"
          [(payloadJson)]="partyDraftJson" />
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="partyDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="savePartyDialog()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
    .section-title h3 { margin: 0; }
    .summary { display: flex; gap: 16px; flex-wrap: wrap; font-size: 14px; margin-bottom: 12px; }
    .lifecycle-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .tabs { display: flex; flex-wrap: wrap; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 10px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; color: #334155; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .lines-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 10px; }
    .lines-header h4 { margin: 0; }
    .lines-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .section-gap { margin-top: 16px; padding-top: 12px; border-top: 1px solid #e5e7eb; }
    .summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .wide { grid-column: 1 / -1; }
    label { display: grid; gap: 6px; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid .wide { grid-column: 1 / -1; }
    .actions { display: flex; gap: 8px; align-items: center; }
    .actions-cell { display: flex; gap: 4px; justify-content: flex-end; white-space: nowrap; }
    .field-hint { font-size: 12px; color: #64748b; margin-top: 8px; }
    .list-grid { display: grid; gap: 8px; margin-top: 10px; }
    .row-card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px; }
    .success { color: #065f46; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) {
      .grid, .summary-grid, .dialog-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class InsuranceQuoteDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(InsuranceApiService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly quote = signal<InsuranceQuoteDetail | null>(null);
  readonly activeTab = signal<QuoteTab>('risk');
  readonly isCreateMode = signal(false);
  readonly saving = signal(false);
  readonly successMessage = signal('');
  readonly errorMessage = signal('');
  readonly products = signal<InsuranceProductListItem[]>([]);
  readonly versions = signal<InsuranceProductVersionListItem[]>([]);
  readonly coverageDefinitions = signal<InsuranceCoverageItem[]>([]);
  readonly optionDrafts = signal<Array<{ optionLabel: string; sumInsured: number; factor: number }>>([]);
  readonly optionDialogVisible = signal(false);
  readonly convertDialogVisible = signal(false);
  readonly riskObjectDialogVisible = signal(false);
  readonly coverageDialogVisible = signal(false);
  readonly partyDialogVisible = signal(false);
  readonly riskObjectItems = signal<JsonArrayItem[]>([]);
  readonly selectedCoverageItems = signal<JsonArrayItem[]>([]);
  readonly convertInsuredParties = signal<JsonArrayItem[]>([]);
  readonly convertBeneficiaries = signal<JsonArrayItem[]>([]);

  readonly riskObjectRows = computed(() => parseRiskObjectsJson(this.quote()?.riskObjectsJson));
  readonly selectedCoverageRows = computed(() => parseSelectedCoveragesJson(this.quote()?.selectedCoveragesJson));

  readonly riskObjectFields = QUOTE_RISK_OBJECT_ITEM_FIELDS;
  readonly coverageFields = QUOTE_SELECTED_COVERAGE_ITEM_FIELDS;
  readonly pricingSnapshotFields = QUOTE_PRICING_SNAPSHOT_FIELDS;

  selectedProductId: string | null = null;
  optionForm = { optionLabel: '', sumInsured: 10000000, factor: 1 };
  createForm: CreateInsuranceQuotePayload = {
    legalEntityId: '',
    productVersionId: '',
    customerAccountId: '',
    channelId: null,
    producerId: null,
    validUntilUtc: new Date(Date.now() + (5 * 24 * 60 * 60 * 1000)).toISOString(),
    riskObjectsJson: '[]',
    selectedCoveragesJson: '[]'
  };
  convertForm = {
    policyholderPartyId: '',
    paymentPlanPreference: 'monthly' as string | null
  };
  priceCurrencyCode = 'IRR';
  priceSumInsured = 10000000;
  priceFactor = 1;

  riskObjectDraftJson = '{}';
  coverageDraftJson = '{}';
  partyDraftJson = '{}';
  private riskObjectDialogMode: ArrayDialogMode = 'create';
  private coverageDialogMode: ArrayDialogMode = 'create';
  private partyDialogMode: ArrayDialogMode = 'create';
  private partyDialogTarget: 'insured' | 'beneficiary' = 'insured';
  private riskObjectDialogIndex = -1;
  private coverageDialogIndex = -1;
  private partyDialogIndex = -1;

  ngOnInit(): void {
    this.api.listProducts().subscribe({
      next: rows => {
        this.products.set(rows);
        if (this.isCreateMode()) {
          this.selectedProductId = rows[0]?.id ?? null;
          this.onProductChanged(this.selectedProductId);
        } else {
          this.tryResolveProductContext();
        }
      },
      error: () => {
        this.products.set([]);
        this.versions.set([]);
      }
    });

    const quoteId = this.route.snapshot.paramMap.get('quoteId');
    const isNewRoute = this.route.snapshot.url.at(-1)?.path === 'new';
    if (isNewRoute) {
      this.isCreateMode.set(true);
      this.loading.set(false);
      return;
    }

    if (!quoteId) {
      this.loading.set(false);
      return;
    }

    this.api.getQuote(quoteId).subscribe({
      next: item => {
        this.quote.set(item);
        this.tryResolveProductContext();
        this.loading.set(false);
      },
      error: () => {
        this.quote.set(null);
        this.loading.set(false);
      }
    });
  }

  pageTitle(): string {
    return this.isCreateMode()
      ? this.t('insurance.quotes.form.createTitle')
      : this.t('insurance.quotes.form.editTitle');
  }

  quoteStatusOptions(): Array<{ label: string; value: string }> {
    return [
      { value: 'Draft', label: this.t('insurance.quotes.status.draft') },
      { value: 'Priced', label: this.t('insurance.quotes.status.priced') },
      { value: 'Expired', label: this.t('insurance.quotes.status.expired') },
      { value: 'ConvertedToProposal', label: this.t('insurance.quotes.status.convertedToProposal') },
      { value: 'Cancelled', label: this.t('insurance.quotes.status.cancelled') }
    ];
  }

  paymentPlanOptions(): Array<{ label: string; value: string }> {
    return PAYMENT_PLAN_OPTIONS.map(item => ({
      value: item.value,
      label: this.t(item.labelKey)
    }));
  }

  coverageCodeSelectOptions(): Record<string, Array<{ label: string; value: string }>> {
    const options = this.coverageDefinitions().map(item => ({
      value: item.code,
      label: `${item.code} — ${item.name}`
    }));
    if (options.length === 0) {
      options.push({ value: 'BASE', label: 'BASE' });
    }
    return { coverageCode: options };
  }

  versionOptions(): Array<{ label: string; value: string }> {
    return this.versions().map(version => ({
      value: version.id,
      label: `v${version.versionNumber} (${version.status})`
    }));
  }

  productOptions(): Array<{ label: string; value: string }> {
    return this.products().map(item => ({
      value: item.id,
      label: `${item.code} - ${item.name}`
    }));
  }

  onProductChanged(productId: string | null): void {
    this.selectedProductId = productId;
    this.createForm.productVersionId = '';
    this.coverageDefinitions.set([]);
    if (!productId) {
      this.versions.set([]);
      return;
    }

    this.api.getProduct(productId).subscribe({
      next: (product: InsuranceProductDetail | null) => {
        const versions = product?.versions ?? [];
        this.versions.set(versions);
        const firstVersionId = versions[0]?.id ?? '';
        this.createForm.productVersionId = firstVersionId;
        this.onVersionChanged(firstVersionId);
      },
      error: () => {
        this.versions.set([]);
      }
    });
  }

  onVersionChanged(versionId: string | null): void {
    if (!this.selectedProductId || !versionId) {
      this.coverageDefinitions.set([]);
      return;
    }
    this.api.listVersionCoverages(this.selectedProductId, versionId).subscribe({
      next: rows => this.coverageDefinitions.set(rows),
      error: () => this.coverageDefinitions.set([])
    });
  }

  private tryResolveProductContext(): void {
    const current = this.quote();
    if (!current?.productVersionId || this.products().length === 0) {
      return;
    }
    if (this.versions().some(item => item.id === current.productVersionId)) {
      return;
    }

    for (const product of this.products()) {
      this.api.getProduct(product.id).subscribe({
        next: detail => {
          if (!detail?.versions?.some(item => item.id === current.productVersionId)) {
            return;
          }
          this.selectedProductId = product.id;
          this.versions.set(detail.versions ?? []);
          this.onVersionChanged(current.productVersionId);
        }
      });
    }
  }

  saveCreate(): void {
    this.syncCreateJsonFields();
    this.errorMessage.set('');
    this.saving.set(true);
    this.api.createQuote(this.createForm).subscribe({
      next: item => {
        this.saving.set(false);
        void this.router.navigate(['/insurance/quotes', item.id]);
      },
      error: () => {
        this.saving.set(false);
        this.errorMessage.set(this.t('insurance.states.loadError'));
      }
    });
  }

  canConvert(): boolean {
    const status = this.quote()?.status;
    return status === 'Draft' || status === 'Priced';
  }

  openConvertDialog(): void {
    const current = this.quote();
    if (!current || !this.canConvert()) {
      return;
    }
    this.convertForm = {
      policyholderPartyId: current.customerAccountId,
      paymentPlanPreference: 'monthly'
    };
    this.convertInsuredParties.set([]);
    this.convertBeneficiaries.set([]);
    this.convertDialogVisible.set(true);
  }

  convertToProposal(): void {
    const current = this.quote();
    if (!current || !this.convertForm.policyholderPartyId.trim()) {
      return;
    }
    this.errorMessage.set('');
    this.saving.set(true);

    const payload: ConvertQuoteToProposalPayload = {
      policyholderPartyId: this.convertForm.policyholderPartyId.trim(),
      insuredPartiesJson: serializeProposalPartyArray(INSURED_PARTY_ITEM_FIELDS, this.convertInsuredParties()),
      beneficiariesJson: serializeProposalPartyArray(BENEFICIARY_ITEM_FIELDS, this.convertBeneficiaries()),
      paymentPlanPreference: this.convertForm.paymentPlanPreference
    };
    this.api.convertQuoteToProposal(current.id, payload).subscribe({
      next: item => {
        this.saving.set(false);
        this.convertDialogVisible.set(false);
        void this.router.navigate(['/insurance/proposals', item.id]);
      },
      error: () => {
        this.saving.set(false);
        this.errorMessage.set(this.t('insurance.states.loadError'));
      }
    });
  }

  priceQuote(): void {
    const current = this.quote();
    if (!current) {
      return;
    }
    this.errorMessage.set('');
    this.successMessage.set('');
    this.saving.set(true);

    const payload: PriceInsuranceQuotePayload = {
      ratingPlanId: null,
      currencyCode: this.priceCurrencyCode,
      factors: { userFactor: this.priceFactor || 1 },
      coverages: [
        {
          coverageCode: 'BASE',
          sumInsured: this.priceSumInsured || 0,
          selected: true
        }
      ]
    };

    this.api.priceQuote(current.id, payload).subscribe({
      next: item => {
        this.quote.set(item);
        this.saving.set(false);
        this.successMessage.set(this.t('insurance.quoteDetail.messages.priced'));
      },
      error: () => {
        this.saving.set(false);
        this.errorMessage.set(this.t('insurance.states.loadError'));
      }
    });
  }

  openOptionCreate(): void {
    this.optionForm = {
      optionLabel: '',
      sumInsured: this.priceSumInsured,
      factor: this.priceFactor
    };
    this.optionDialogVisible.set(true);
  }

  saveOptionDialog(): void {
    if (!this.optionForm.optionLabel.trim()) {
      return;
    }
    this.optionDrafts.update(rows => [...rows, {
      optionLabel: this.optionForm.optionLabel.trim(),
      sumInsured: this.optionForm.sumInsured || 0,
      factor: this.optionForm.factor || 1
    }]);
    this.optionDialogVisible.set(false);
  }

  removeOptionDraft(index: number): void {
    this.optionDrafts.update(rows => rows.filter((_, idx) => idx !== index));
  }

  saveOptions(): void {
    const current = this.quote();
    if (!current || this.optionDrafts().length === 0) {
      return;
    }
    this.errorMessage.set('');
    this.successMessage.set('');
    this.saving.set(true);

    const payload = this.optionDrafts()
      .filter(item => item.optionLabel.trim().length > 0)
      .map(item => ({
        optionLabel: item.optionLabel.trim(),
        coverages: [{ coverageCode: 'BASE', sumInsured: item.sumInsured || 0, selected: true }],
        ratingPlanId: null,
        currencyCode: this.priceCurrencyCode,
        factors: { userFactor: item.factor || 1 }
      }));
    if (payload.length === 0) {
      this.saving.set(false);
      return;
    }

    this.api.createQuoteOptions(current.id, payload).subscribe({
      next: item => {
        this.quote.set(item);
        this.optionDrafts.set([]);
        this.saving.set(false);
        this.successMessage.set(this.t('insurance.quoteDetail.messages.optionsSaved'));
      },
      error: () => {
        this.saving.set(false);
        this.errorMessage.set(this.t('insurance.states.loadError'));
      }
    });
  }

  openRiskObjectCreate(): void {
    this.riskObjectDialogMode = 'create';
    this.riskObjectDialogIndex = -1;
    this.riskObjectDraftJson = JSON.stringify(defaultItemModel(this.riskObjectFields));
    this.riskObjectDialogVisible.set(true);
  }

  openRiskObjectEdit(index: number): void {
    const row = this.riskObjectItems()[index];
    if (!row) {
      return;
    }
    this.riskObjectDialogMode = 'edit';
    this.riskObjectDialogIndex = index;
    this.riskObjectDraftJson = JSON.stringify(mergeItemModel(this.riskObjectFields, row));
    this.riskObjectDialogVisible.set(true);
  }

  removeRiskObject(index: number): void {
    this.riskObjectItems.update(rows => rows.filter((_, idx) => idx !== index));
    this.syncCreateJsonFields();
  }

  saveRiskObjectDialog(): void {
    const item = mergeItemModel(this.riskObjectFields, JSON.parse(this.riskObjectDraftJson || '{}') as JsonArrayItem);
    const next = [...this.riskObjectItems()];
    if (this.riskObjectDialogMode === 'create') {
      next.push(item);
    } else if (this.riskObjectDialogIndex >= 0) {
      next[this.riskObjectDialogIndex] = item;
    }
    this.riskObjectItems.set(next);
    this.syncCreateJsonFields();
    this.riskObjectDialogVisible.set(false);
  }

  riskObjectDialogTitle(): string {
    return this.riskObjectDialogMode === 'create'
      ? this.t('insurance.quoteDetail.dialogs.addRiskObject')
      : this.t('insurance.quoteDetail.dialogs.editRiskObject');
  }

  openCoverageCreate(): void {
    this.coverageDialogMode = 'create';
    this.coverageDialogIndex = -1;
    const model = defaultItemModel(this.coverageFields);
    model['selected'] = true;
    if (!model['coverageCode'] && this.coverageDefinitions()[0]?.code) {
      model['coverageCode'] = this.coverageDefinitions()[0].code;
    }
    this.coverageDraftJson = JSON.stringify(model);
    this.coverageDialogVisible.set(true);
  }

  openCoverageEdit(index: number): void {
    const row = this.selectedCoverageItems()[index];
    if (!row) {
      return;
    }
    this.coverageDialogMode = 'edit';
    this.coverageDialogIndex = index;
    this.coverageDraftJson = JSON.stringify(mergeItemModel(this.coverageFields, row));
    this.coverageDialogVisible.set(true);
  }

  removeCoverage(index: number): void {
    this.selectedCoverageItems.update(rows => rows.filter((_, idx) => idx !== index));
    this.syncCreateJsonFields();
  }

  saveCoverageDialog(): void {
    const item = mergeItemModel(this.coverageFields, JSON.parse(this.coverageDraftJson || '{}') as JsonArrayItem);
    if (!`${item['coverageCode'] ?? ''}`.trim()) {
      return;
    }
    const next = [...this.selectedCoverageItems()];
    if (this.coverageDialogMode === 'create') {
      next.push(item);
    } else if (this.coverageDialogIndex >= 0) {
      next[this.coverageDialogIndex] = item;
    }
    this.selectedCoverageItems.set(next);
    this.syncCreateJsonFields();
    this.coverageDialogVisible.set(false);
  }

  coverageDialogTitle(): string {
    return this.coverageDialogMode === 'create'
      ? this.t('insurance.quoteDetail.dialogs.addCoverage')
      : this.t('insurance.quoteDetail.dialogs.editCoverage');
  }

  openInsuredPartyCreate(): void {
    this.partyDialogTarget = 'insured';
    this.partyDialogMode = 'create';
    this.partyDialogIndex = -1;
    this.partyDraftJson = JSON.stringify(defaultItemModel(INSURED_PARTY_ITEM_FIELDS));
    this.partyDialogVisible.set(true);
  }

  openInsuredPartyEdit(index: number): void {
    const row = this.convertInsuredParties()[index];
    if (!row) {
      return;
    }
    this.partyDialogTarget = 'insured';
    this.partyDialogMode = 'edit';
    this.partyDialogIndex = index;
    this.partyDraftJson = JSON.stringify(mergeItemModel(INSURED_PARTY_ITEM_FIELDS, row));
    this.partyDialogVisible.set(true);
  }

  removeInsuredParty(index: number): void {
    this.convertInsuredParties.update(rows => rows.filter((_, idx) => idx !== index));
  }

  openBeneficiaryCreate(): void {
    this.partyDialogTarget = 'beneficiary';
    this.partyDialogMode = 'create';
    this.partyDialogIndex = -1;
    this.partyDraftJson = JSON.stringify(defaultItemModel(BENEFICIARY_ITEM_FIELDS));
    this.partyDialogVisible.set(true);
  }

  openBeneficiaryEdit(index: number): void {
    const row = this.convertBeneficiaries()[index];
    if (!row) {
      return;
    }
    this.partyDialogTarget = 'beneficiary';
    this.partyDialogMode = 'edit';
    this.partyDialogIndex = index;
    this.partyDraftJson = JSON.stringify(mergeItemModel(BENEFICIARY_ITEM_FIELDS, row));
    this.partyDialogVisible.set(true);
  }

  removeBeneficiary(index: number): void {
    this.convertBeneficiaries.update(rows => rows.filter((_, idx) => idx !== index));
  }

  savePartyDialog(): void {
    const fields = this.partyDialogFields();
    const item = mergeItemModel(fields, JSON.parse(this.partyDraftJson || '{}') as JsonArrayItem);
    if (!`${item['partyId'] ?? ''}`.trim()) {
      return;
    }
    const target = this.partyDialogTarget === 'insured' ? this.convertInsuredParties : this.convertBeneficiaries;
    const next = [...target()];
    if (this.partyDialogMode === 'create') {
      next.push(item);
    } else if (this.partyDialogIndex >= 0) {
      next[this.partyDialogIndex] = item;
    }
    target.set(next);
    this.partyDialogVisible.set(false);
  }

  partyDialogFields(): typeof INSURED_PARTY_ITEM_FIELDS {
    return this.partyDialogTarget === 'insured' ? INSURED_PARTY_ITEM_FIELDS : BENEFICIARY_ITEM_FIELDS;
  }

  partyDialogTitle(): string {
    if (this.partyDialogTarget === 'insured') {
      return this.partyDialogMode === 'create'
        ? this.t('insurance.proposalDetail.dialogs.addInsuredParty')
        : this.t('insurance.proposalDetail.dialogs.editInsuredParty');
    }
    return this.partyDialogMode === 'create'
      ? this.t('insurance.proposalDetail.dialogs.addBeneficiary')
      : this.t('insurance.proposalDetail.dialogs.editBeneficiary');
  }

  optionCoverageSummary(raw: string): string {
    const rows = parseSelectedCoveragesJson(raw);
    if (rows.length === 0) {
      return '-';
    }
    return rows.map(row => coverageSummary(row)).join('; ');
  }

  coveragePayloadJson(item: JsonArrayItem): string {
    return JSON.stringify(mergeItemModel(this.coverageFields, item));
  }

  riskObjectPayloadJson(item: JsonArrayItem): string {
    return JSON.stringify(mergeItemModel(this.riskObjectFields, item));
  }

  formatBoolean(value: unknown): string {
    return formatBooleanDisplay(value, this.t('common.yes'), this.t('common.no'));
  }

  private syncCreateJsonFields(): void {
    this.createForm.riskObjectsJson = serializeJsonArray(this.riskObjectItems());
    this.createForm.selectedCoveragesJson = serializeJsonArray(this.selectedCoverageItems());
  }

  statusKey(status: InsuranceQuoteDetail['status']): string {
    return `insurance.quotes.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
